import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Block } from '../types';
import Toolbar, { ElementPalette } from './richtext/Toolbar';
import Inspector from './richtext/Inspector';
import FindReplace, { SearchHit, SearchOptions, collectHits, searchPattern } from './richtext/FindReplace';
import { DesignerContext, caretKey } from './richtext/context';
import { NodeFrame, NodeView, styleToCss } from './richtext/nodes';
import { EditorApi } from './richtext/commands';
import { TextRange } from './richtext/RichTextInput';
import {
  BooleanMarkKey, DEFAULT_PAGE, DesignerDocument, DesignerNode, DesignerStyle, MarkFlags, MarkKey, PX_PER_MM, ParagraphStyleDef,
  activeMarks, applyMarks, clearMarks, clone, documentStatistics, headingNumbers, insertText, isTextNode,
  pageGeometry, removeRange, renewIds, resolveStyle, sliceMarks, toggleMark, uid,
} from './richtext/model';

export type { DesignerStyle, TextMark, DesignerNode, DesignerDocument, ListItem, PageSetup, ParagraphStyleDef } from './richtext/model';
export { uid, documentToPlainText, emptyDocument, resolveStyle } from './richtext/model';

// ==================== Baumoperationen ====================

const findIn = (nodes: DesignerNode[], id: string): DesignerNode | undefined => {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.type === 'container') {
      for (const column of node.columns || []) {
        const found = findIn(column.nodes || [], id);
        if (found) return found;
      }
    }
  }
  return undefined;
};

const updateIn = (nodes: DesignerNode[], id: string, patch: Partial<DesignerNode>): DesignerNode[] =>
  nodes.map(node => {
    if (node.id === id) return { ...node, ...patch };
    if (node.type === 'container') return { ...node, columns: (node.columns || []).map((column: any) => ({ ...column, nodes: updateIn(column.nodes || [], id, patch) })) };
    return node;
  });

const removeIn = (nodes: DesignerNode[], id: string): DesignerNode[] =>
  nodes.filter(node => node.id !== id).map(node =>
    node.type === 'container' ? { ...node, columns: (node.columns || []).map((column: any) => ({ ...column, nodes: removeIn(column.nodes || [], id) })) } : node);

/** Wendet eine Änderung auf die Geschwisterliste des Knotens an – auch in Spalten. */
function withSiblings(nodes: DesignerNode[], id: string, transform: (list: DesignerNode[], index: number) => DesignerNode[]): DesignerNode[] | null {
  const index = nodes.findIndex(node => node.id === id);
  if (index >= 0) return transform(nodes, index);
  for (let position = 0; position < nodes.length; position++) {
    const node = nodes[position];
    if (node.type !== 'container') continue;
    const columns = node.columns || [];
    for (let columnIndex = 0; columnIndex < columns.length; columnIndex++) {
      const result = withSiblings(columns[columnIndex].nodes || [], id, transform);
      if (!result) continue;
      const nextColumns = columns.map((column: any, index2: number) => (index2 === columnIndex ? { ...column, nodes: result } : column));
      const next = [...nodes];
      next[position] = { ...node, columns: nextColumns };
      return next;
    }
  }
  return null;
}

const TEXT_PATCH_KEYS = new Set(['text', 'marks', 'items']);

export interface DocumentDesignerProps {
  value: DesignerDocument;
  onChange: (document: DesignerDocument) => void;
  blocks?: Block[];
  /** Bekannte Platzhalternamen für das Einfügen-Menü. */
  variables?: string[];
  readOnly?: boolean;
}

/**
 * Vollwertiger Dokumenteditor für Verträge, Vorlagen und Bausteine:
 * Zeichen- und Absatzformate, Formatvorlagen, Listen, Tabellen mit Formeln,
 * Bilder, Seiteneinrichtung, Suchen/Ersetzen und Seitenvorschau.
 */
export default function DocumentDesigner({ value, onChange, blocks = [], variables = [], readOnly }: DocumentDesignerProps) {
  const [selected, setSelected] = useState<string | undefined>();
  const [activeItemId, setActiveItemId] = useState<string | undefined>();
  const [textSelection, setTextSelection] = useState<{ nodeId: string; itemId?: string; from: number; to: number } | null>(null);
  const [syncTokens, setSyncTokens] = useState<Record<string, number>>({});
  const [caret, setCaret] = useState<{ key: string; range: TextRange } | null>(null);
  const [autoFocusKey, setAutoFocusKey] = useState<string | null>(null);
  const [dragged, setDragged] = useState<string | undefined>();
  const [zoom, setZoom] = useState(1);
  const [painter, setPainter] = useState<{ style?: DesignerStyle; flags?: MarkFlags } | null>(null);
  const [showFind, setShowFind] = useState(false);
  const [search, setSearch] = useState<SearchOptions>({ query: '', replacement: '', caseSensitive: false, wholeWord: false });
  const [hitIndex, setHitIndex] = useState(0);
  const [historyTick, setHistoryTick] = useState(0);
  const [pageCount, setPageCount] = useState(1);

  const history = useRef<DesignerDocument[]>([]);
  const future = useRef<DesignerDocument[]>([]);
  const lastCommit = useRef({ label: '', time: 0 });
  const latest = useRef(value);
  latest.current = value;
  const flow = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLDivElement>(null);
  const autoZoom = useRef(true);

  const page = value.page || DEFAULT_PAGE;
  const geometry = pageGeometry(page);
  const numbers = useMemo(() => headingNumbers(value), [value]);
  const statistics = useMemo(() => documentStatistics(value), [value]);
  const node = selected ? findIn(value.nodes, selected) : undefined;

  const commit = useCallback((next: DesignerDocument, label = 'edit') => {
    const now = Date.now();
    const coalesce = label === 'text' && lastCommit.current.label === 'text' && now - lastCommit.current.time < 700;
    if (!coalesce) {
      history.current.push(clone(latest.current));
      if (history.current.length > 200) history.current.shift();
      future.current = [];
    }
    lastCommit.current = { label, time: now };
    setHistoryTick(tick => tick + 1);
    onChange(next);
  }, [onChange]);

  const undo = useCallback(() => {
    const previous = history.current.pop();
    if (!previous) return;
    future.current.push(clone(latest.current));
    lastCommit.current = { label: 'undo', time: Date.now() };
    setHistoryTick(tick => tick + 1);
    onChange(previous);
  }, [onChange]);

  const redo = useCallback(() => {
    const next = future.current.pop();
    if (!next) return;
    history.current.push(clone(latest.current));
    lastCommit.current = { label: 'redo', time: Date.now() };
    setHistoryTick(tick => tick + 1);
    onChange(next);
  }, [onChange]);

  const bump = useCallback((key: string, range?: TextRange) => {
    setSyncTokens(tokens => ({ ...tokens, [key]: (tokens[key] || 0) + 1 }));
    setCaret(range ? { key, range } : null);
  }, []);

  const update = useCallback((id: string, patch: Partial<DesignerNode>) => {
    const label = Object.keys(patch).every(key => TEXT_PATCH_KEYS.has(key)) ? 'text' : 'edit';
    commit({ ...latest.current, nodes: updateIn(latest.current.nodes, id, patch) }, label);
  }, [commit]);

  const setNodes = useCallback((nodes: DesignerNode[], label = 'edit') => commit({ ...latest.current, nodes }, label), [commit]);

  // ---------- aktive Textstelle ----------

  const target = useMemo(() => {
    if (!node) return null;
    if (isTextNode(node)) return { text: node.text || '', marks: node.marks || [], itemId: undefined as string | undefined };
    if (node.type === 'list') {
      const item = (node.items || []).find(entry => entry.id === (textSelection?.itemId || activeItemId)) || (node.items || [])[0];
      if (item) return { text: item.text || '', marks: item.marks || [], itemId: item.id };
    }
    return null;
  }, [node, textSelection, activeItemId]);

  const range = textSelection && node && textSelection.nodeId === node.id ? textSelection : null;
  const hasSelection = !!range && range.to > range.from;

  const writeText = useCallback((nodeId: string, itemId: string | undefined, text: string, marks: any[], caretRange?: TextRange) => {
    const current = findIn(latest.current.nodes, nodeId);
    if (!current) return;
    if (itemId && current.type === 'list') {
      update(nodeId, { items: (current.items || []).map(entry => (entry.id === itemId ? { ...entry, text, marks } : entry)) });
    } else {
      update(nodeId, { text, marks });
    }
    bump(caretKey(nodeId, itemId), caretRange);
  }, [update, bump]);

  const flags: MarkFlags = useMemo(() => {
    if (!target || !range) return {};
    if (range.to > range.from) return activeMarks(target.text, target.marks, range.from, range.to);
    return activeMarks(target.text, target.marks, Math.max(0, range.from - 1), range.from);
  }, [target, range]);

  const resolvedStyle = useMemo(() => resolveStyle(value, node?.style), [value, node]);

  // ---------- Befehle ----------

  const doToggleMark = useCallback((key: BooleanMarkKey) => {
    if (!node || !target) return;
    if (range && range.to > range.from) {
      writeText(node.id, target.itemId, target.text, toggleMark(target.text, target.marks, range.from, range.to, key), { from: range.from, to: range.to });
      return;
    }
    if (['bold', 'italic', 'underline', 'strike'].includes(key)) {
      update(node.id, { style: { ...node.style, [key]: !(node.style as any)?.[key] } });
      return;
    }
    writeText(node.id, target.itemId, target.text, toggleMark(target.text, target.marks, 0, target.text.length, key));
  }, [node, target, range, writeText, update]);

  const setMark = useCallback((key: MarkKey, markValue: string | number | undefined) => {
    if (!node || !target) return;
    if (range && range.to > range.from) {
      writeText(node.id, target.itemId, target.text, applyMarks(target.text, target.marks, range.from, range.to, { [key]: markValue } as any), { from: range.from, to: range.to });
      return;
    }
    if (key === 'link') return;
    update(node.id, { style: { ...node.style, [key]: markValue } as DesignerStyle });
  }, [node, target, range, writeText, update]);

  const setStyle = useCallback((patch: DesignerStyle) => {
    if (!node) return;
    update(node.id, { style: { ...node.style, ...patch } });
  }, [node, update]);

  const setDocumentNode = useCallback((patch: Partial<DesignerNode>) => {
    if (!node) return;
    update(node.id, patch);
  }, [node, update]);

  const setDocument = useCallback((patch: Partial<DesignerDocument>) => commit({ ...latest.current, ...patch }), [commit]);

  const clearFormatting = useCallback(() => {
    if (!node || !target) { if (node) update(node.id, { style: {} }); return; }
    if (range && range.to > range.from) {
      writeText(node.id, target.itemId, target.text, clearMarks(target.text, target.marks, range.from, range.to), { from: range.from, to: range.to });
      return;
    }
    if (target.itemId) writeText(node.id, target.itemId, target.text, []);
    else update(node.id, { style: {}, marks: [] });
  }, [node, target, range, writeText, update]);

  const insert = useCallback((fresh: DesignerNode) => {
    const nodes = selected
      ? withSiblings(latest.current.nodes, selected, (list, index) => { const next = [...list]; next.splice(index + 1, 0, fresh); return next; }) || [...latest.current.nodes, fresh]
      : [...latest.current.nodes, fresh];
    setNodes(nodes);
    setSelected(fresh.id);
    setActiveItemId(fresh.type === 'list' ? fresh.items?.[0]?.id : undefined);
    if (isTextNode(fresh)) setAutoFocusKey(caretKey(fresh.id));
    else if (fresh.type === 'list' && fresh.items?.[0]) setAutoFocusKey(caretKey(fresh.id, fresh.items[0].id));
  }, [selected, setNodes]);

  const convert = useCallback((type: 'paragraph' | 'heading', level?: 1 | 2 | 3 | 4 | 5 | 6) => {
    if (!node) return;
    if (node.type === 'list') {
      const paragraphs: DesignerNode[] = (node.items || []).map(item => ({ id: uid(), type, level, text: item.text, marks: item.marks, style: node.style }));
      const nodes = withSiblings(latest.current.nodes, node.id, (list, index) => { const next = [...list]; next.splice(index, 1, ...paragraphs); return next; });
      if (nodes) { setNodes(nodes); setSelected(paragraphs[0]?.id); }
      return;
    }
    if (!isTextNode(node)) return;
    update(node.id, { type, level: type === 'heading' ? (level || 1) : undefined });
  }, [node, update, setNodes]);

  const toggleList = useCallback((ordered: boolean) => {
    if (!node) return;
    if (node.type === 'list') {
      if (!!node.ordered === ordered) { convert('paragraph'); return; }
      update(node.id, { ordered, marker: undefined });
      return;
    }
    if (!isTextNode(node)) return;
    const list: DesignerNode = { id: uid(), type: 'list', ordered, start: ordered ? 1 : undefined, style: node.style, items: [{ id: uid(), text: node.text || '', marks: node.marks || [], level: 0 }] };
    const nodes = withSiblings(latest.current.nodes, node.id, (entries, index) => { const next = [...entries]; next.splice(index, 1, list); return next; });
    if (nodes) { setNodes(nodes); setSelected(list.id); setActiveItemId(list.items![0].id); setAutoFocusKey(caretKey(list.id, list.items![0].id)); }
  }, [node, update, convert, setNodes]);

  const indent = useCallback((outdent: boolean) => {
    if (!node) return;
    if (node.type === 'list' && (activeItemId || node.items?.length)) {
      const itemId = activeItemId || node.items![0].id;
      update(node.id, {
        items: (node.items || []).map(item => (item.id === itemId ? { ...item, level: Math.max(0, Math.min(8, (item.level || 0) + (outdent ? -1 : 1))) } : item)),
      });
      return;
    }
    const current = node.style?.indentLeft || 0;
    setStyle({ indentLeft: Math.max(0, current + (outdent ? -20 : 20)) });
  }, [node, activeItemId, update, setStyle]);

  const applyStyleRef = useCallback((id?: string) => {
    if (!node) return;
    update(node.id, { style: { ...node.style, styleRef: id } });
  }, [node, update]);

  const styleWithoutRef = (style?: DesignerStyle) => { const copy = { ...(style || {}) }; delete copy.styleRef; return copy; };

  const createStyleFromNode = useCallback((name: string) => {
    if (!node) return;
    const entry: ParagraphStyleDef = { id: uid(), name, appliesTo: 'any', style: styleWithoutRef(resolveStyle(latest.current, node.style)) };
    commit({ ...latest.current, styles: [...(latest.current.styles || []), entry], nodes: updateIn(latest.current.nodes, node.id, { style: { styleRef: entry.id } }) });
  }, [node, commit]);

  const updateStyleFromNode = useCallback((id: string) => {
    if (!node) return;
    const merged = styleWithoutRef(resolveStyle(latest.current, node.style));
    commit({
      ...latest.current,
      styles: (latest.current.styles || []).map(entry => (entry.id === id ? { ...entry, style: merged } : entry)),
      nodes: updateIn(latest.current.nodes, node.id, { style: { styleRef: id } }),
    });
  }, [node, commit]);

  const copyFormat = useCallback(() => {
    if (!node) return;
    setPainter({ style: clone(node.style || {}), flags: clone(flags) });
  }, [node, flags]);

  const pasteFormat = useCallback(() => {
    if (!node || !painter) return;
    if (range && range.to > range.from && target && painter.flags) {
      writeText(node.id, target.itemId, target.text, applyMarks(target.text, target.marks, range.from, range.to, painter.flags), { from: range.from, to: range.to });
    }
    if (painter.style) update(node.id, { style: { ...painter.style } });
    setPainter(null);
  }, [node, painter, range, target, writeText, update]);

  const insertVariable = useCallback((name: string) => {
    const token = `<<${name}>>`;
    if (node && target && range) {
      const cleaned = removeRange(target.text, target.marks, range.from, range.to);
      const next = insertText(cleaned.text, cleaned.marks, range.from, token, flags);
      writeText(node.id, target.itemId, next.text, next.marks, { from: range.from + token.length, to: range.from + token.length });
      return;
    }
    insert({ id: uid(), type: 'paragraph', text: token });
  }, [node, target, range, flags, writeText, insert]);

  const insertLink = useCallback((href?: string) => {
    if (!node || !target || !range) return;
    const url = href ?? window.prompt('Adresse der Verknüpfung', flags.link || 'https://');
    if (url === null) return;
    if (range.to > range.from) {
      writeText(node.id, target.itemId, target.text, applyMarks(target.text, target.marks, range.from, range.to, { link: url || undefined }), { from: range.from, to: range.to });
      return;
    }
    if (!url) return;
    const next = insertText(target.text, target.marks, range.from, url, { ...flags, link: url });
    writeText(node.id, target.itemId, next.text, next.marks, { from: range.from + url.length, to: range.from + url.length });
  }, [node, target, range, flags, writeText]);

  // ---------- Absätze teilen und verbinden ----------

  const onSplit = useCallback((nodeId: string, itemId: string | undefined, at: TextRange) => {
    const current = findIn(latest.current.nodes, nodeId);
    if (!current) return;
    if (itemId && current.type === 'list') {
      const items = current.items || [];
      const index = items.findIndex(item => item.id === itemId);
      const item = items[index];
      if (!item) return;
      const left = { ...item, text: item.text.slice(0, at.from), marks: sliceMarks(item.text, item.marks, 0, at.from) };
      const rest = item.text.slice(at.to);
      if (!item.text.trim() && (item.level || 0) === 0) {
        // Leerer Eintrag am Listenende beendet die Liste.
        const paragraph: DesignerNode = { id: uid(), type: 'paragraph', text: '' };
        const nodes = withSiblings(latest.current.nodes, nodeId, (list, position) => {
          const next = [...list];
          next[position] = { ...current, items: items.filter(entry => entry.id !== itemId) };
          next.splice(position + 1, 0, paragraph);
          return next;
        });
        if (nodes) { setNodes(nodes); setSelected(paragraph.id); setAutoFocusKey(caretKey(paragraph.id)); }
        return;
      }
      const fresh = { id: uid(), text: rest, marks: sliceMarks(item.text, item.marks, at.to, item.text.length), level: item.level };
      update(nodeId, { items: [...items.slice(0, index), left, fresh, ...items.slice(index + 1)] });
      setActiveItemId(fresh.id);
      setAutoFocusKey(caretKey(nodeId, fresh.id));
      return;
    }
    const text = current.text || '';
    const marks = current.marks || [];
    const fresh: DesignerNode = {
      id: uid(),
      type: 'paragraph',
      text: text.slice(at.to),
      marks: sliceMarks(text, marks, at.to, text.length),
      style: current.type === 'heading' ? undefined : current.style,
    };
    const nodes = withSiblings(latest.current.nodes, nodeId, (list, position) => {
      const next = [...list];
      next[position] = { ...current, text: text.slice(0, at.from), marks: sliceMarks(text, marks, 0, at.from) };
      next.splice(position + 1, 0, fresh);
      return next;
    });
    if (!nodes) return;
    setNodes(nodes);
    setSelected(fresh.id);
    setAutoFocusKey(caretKey(fresh.id));
    bump(caretKey(nodeId));
  }, [update, setNodes, bump]);

  const onMergeBackward = useCallback((nodeId: string, itemId?: string) => {
    const current = findIn(latest.current.nodes, nodeId);
    if (!current) return;
    if (itemId && current.type === 'list') {
      const items = current.items || [];
      const index = items.findIndex(item => item.id === itemId);
      const item = items[index];
      if (!item) return;
      if ((item.level || 0) > 0) { indent(true); return; }
      if (index === 0) { convert('paragraph'); return; }
      const previous = items[index - 1];
      const offset = previous.text.length;
      const shifted = (item.marks || []).map(mark => ({ ...mark, from: mark.from + offset, to: mark.to + offset }));
      update(nodeId, { items: [...items.slice(0, index - 1), { ...previous, text: previous.text + item.text, marks: [...(previous.marks || []), ...shifted] }, ...items.slice(index + 1)] });
      setActiveItemId(previous.id);
      bump(caretKey(nodeId, previous.id), { from: offset, to: offset });
      return;
    }
    const mergedInto: { id: string; offset: number }[] = [];
    const nodes = withSiblings(latest.current.nodes, nodeId, (list, position) => {
      const previous = list[position - 1];
      if (!previous || !isTextNode(previous)) return list;
      const previousText = previous.text || '';
      mergedInto.push({ id: previous.id, offset: previousText.length });
      const shifted = (current.marks || []).map(mark => ({ ...mark, from: mark.from + previousText.length, to: mark.to + previousText.length }));
      const next = [...list];
      next[position - 1] = { ...previous, text: previousText + (current.text || ''), marks: [...(previous.marks || []), ...shifted] };
      next.splice(position, 1);
      return next;
    });
    const merge = mergedInto[0];
    if (!nodes || !merge) return;
    setNodes(nodes);
    setSelected(merge.id);
    bump(caretKey(merge.id), { from: merge.offset, to: merge.offset });
  }, [update, setNodes, bump, indent, convert]);

  const onMergeForward = useCallback((nodeId: string, itemId?: string) => {
    const current = findIn(latest.current.nodes, nodeId);
    if (!current) return;
    if (itemId && current.type === 'list') {
      const items = current.items || [];
      const index = items.findIndex(item => item.id === itemId);
      const next = items[index + 1];
      if (!next) return;
      const item = items[index];
      const shifted = (next.marks || []).map(mark => ({ ...mark, from: mark.from + item.text.length, to: mark.to + item.text.length }));
      update(nodeId, { items: [...items.slice(0, index), { ...item, text: item.text + next.text, marks: [...(item.marks || []), ...shifted] }, ...items.slice(index + 2)] });
      bump(caretKey(nodeId, item.id), { from: item.text.length, to: item.text.length });
      return;
    }
    const offset = (current.text || '').length;
    const nodes = withSiblings(latest.current.nodes, nodeId, (list, position) => {
      const following = list[position + 1];
      if (!following || !isTextNode(following)) return list;
      const shifted = (following.marks || []).map(mark => ({ ...mark, from: mark.from + offset, to: mark.to + offset }));
      const next = [...list];
      next[position] = { ...current, text: (current.text || '') + (following.text || ''), marks: [...(current.marks || []), ...shifted] };
      next.splice(position + 1, 1);
      return next;
    });
    if (!nodes) return;
    setNodes(nodes);
    bump(caretKey(nodeId), { from: offset, to: offset });
  }, [update, setNodes, bump]);

  const onIndent = useCallback((nodeId: string, itemId: string | undefined, outdent: boolean) => {
    const current = findIn(latest.current.nodes, nodeId);
    if (!current || current.type !== 'list' || !itemId) return false;
    update(nodeId, { items: (current.items || []).map(item => (item.id === itemId ? { ...item, level: Math.max(0, Math.min(8, (item.level || 0) + (outdent ? -1 : 1))) } : item)) });
    bump(caretKey(nodeId, itemId));
    return true;
  }, [update, bump]);

  // ---------- Struktur ----------

  const remove = useCallback((id: string) => {
    setNodes(removeIn(latest.current.nodes, id));
    if (selected === id) setSelected(undefined);
  }, [selected, setNodes]);

  const duplicate = useCallback((id: string) => {
    const source = findIn(latest.current.nodes, id);
    if (!source) return;
    const copy = renewIds(source);
    const nodes = withSiblings(latest.current.nodes, id, (list, index) => { const next = [...list]; next.splice(index + 1, 0, copy); return next; });
    if (nodes) { setNodes(nodes); setSelected(copy.id); }
  }, [setNodes]);

  const move = useCallback((id: string, delta: number) => {
    const nodes = withSiblings(latest.current.nodes, id, (list, index) => {
      const to = index + delta;
      if (to < 0 || to >= list.length) return list;
      const next = [...list];
      const [item] = next.splice(index, 1);
      next.splice(to, 0, item);
      return next;
    });
    if (nodes) setNodes(nodes);
  }, [setNodes]);

  const reorder = useCallback((targetId: string) => {
    if (!dragged || dragged === targetId) return;
    const item = findIn(latest.current.nodes, dragged);
    if (!item) return;
    const without = removeIn(latest.current.nodes, dragged);
    const nodes = withSiblings(without, targetId, (list, index) => { const next = [...list]; next.splice(index, 0, item); return next; });
    if (nodes) setNodes(nodes);
    setDragged(undefined);
  }, [dragged, setNodes]);

  const moveInto = useCallback((containerId: string, columnId: string) => {
    if (!dragged || dragged === containerId) return;
    const item = findIn(latest.current.nodes, dragged);
    if (!item) return;
    const without = removeIn(latest.current.nodes, dragged);
    setNodes(updateIn(without, containerId, {
      columns: (findIn(without, containerId)?.columns || []).map((column: any) => (column.id === columnId ? { ...column, nodes: [...(column.nodes || []), item] } : column)),
    }));
    setDragged(undefined);
  }, [dragged, setNodes]);

  const addBlock = useCallback((block: Block) => {
    let fresh: DesignerNode[] = [];
    let blockDefaults: DesignerStyle | undefined;
    if (block.documentJson) {
      try {
        const parsed = JSON.parse(block.documentJson) as DesignerDocument;
        fresh = parsed.nodes || [];
        blockDefaults = parsed.defaults;
      } catch { /* Klartext-Fallback */ }
    }
    if (!fresh.length) fresh = [{ id: uid(), type: 'paragraph', text: block.content }];
    // Die Dokumentvorgaben des Bausteins gelten im Zieldokument nicht mehr und wandern
    // deshalb an die eingefügten Elemente, damit die Formatierung erhalten bleibt.
    const withDefaults = (node: DesignerNode): DesignerNode => {
      if (!blockDefaults) return node;
      const next: DesignerNode = { ...node, style: { ...blockDefaults, ...(node.style || {}) } };
      if (next.type === 'container' && next.columns) {
        next.columns = next.columns.map((column: any) => ({ ...column, nodes: (column.nodes || []).map(withDefaults) }));
      }
      return next;
    };
    setNodes([...latest.current.nodes, ...fresh.map(node => renewIds(withDefaults(node)))]);
  }, [setNodes]);

  // ---------- Suchen und Ersetzen ----------

  const hits = useMemo(() => (showFind ? collectHits(value, search) : []), [showFind, value, search]);
  useEffect(() => { setHitIndex(index => (hits.length ? Math.min(index, hits.length - 1) : 0)); }, [hits.length]);

  const focusHit = useCallback((hit?: SearchHit) => {
    if (!hit) return;
    setSelected(hit.nodeId);
    if (hit.itemId) setActiveItemId(hit.itemId);
    if (!hit.cellKey) {
      setTextSelection({ nodeId: hit.nodeId, itemId: hit.itemId, from: hit.from, to: hit.to });
      bump(caretKey(hit.nodeId, hit.itemId), { from: hit.from, to: hit.to });
    }
    document.querySelector(`[data-node-id="${hit.nodeId}"]`)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [bump]);

  const navigateHit = useCallback((delta: number) => {
    if (!hits.length) return;
    const next = (hitIndex + delta + hits.length) % hits.length;
    setHitIndex(next);
    focusHit(hits[next]);
  }, [hits, hitIndex, focusHit]);

  const replaceHit = useCallback((hit: SearchHit, document_: DesignerDocument): DesignerDocument => {
    const current = findIn(document_.nodes, hit.nodeId);
    if (!current) return document_;
    if (hit.cellKey) {
      return {
        ...document_,
        nodes: updateIn(document_.nodes, hit.nodeId, {
          rows: (current.rows || []).map((row: any) => (row.id === hit.itemId
            ? { ...row, cells: { ...row.cells, [hit.cellKey!]: String(row.cells?.[hit.cellKey!] ?? '').slice(0, hit.from) + search.replacement + String(row.cells?.[hit.cellKey!] ?? '').slice(hit.to) } }
            : row)),
        }),
      };
    }
    if (hit.itemId && current.type === 'list') {
      const items = (current.items || []).map(item => {
        if (item.id !== hit.itemId) return item;
        const removed = removeRange(item.text, item.marks, hit.from, hit.to);
        const inserted = insertText(removed.text, removed.marks, hit.from, search.replacement, activeMarks(item.text, item.marks, hit.from, hit.to));
        return { ...item, text: inserted.text, marks: inserted.marks };
      });
      return { ...document_, nodes: updateIn(document_.nodes, hit.nodeId, { items }) };
    }
    const text = current.text || '';
    const removed = removeRange(text, current.marks || [], hit.from, hit.to);
    const inserted = insertText(removed.text, removed.marks, hit.from, search.replacement, activeMarks(text, current.marks || [], hit.from, hit.to));
    return { ...document_, nodes: updateIn(document_.nodes, hit.nodeId, { text: inserted.text, marks: inserted.marks }) };
  }, [search.replacement]);

  const replaceCurrent = useCallback(() => {
    const hit = hits[hitIndex];
    if (!hit) return;
    commit(replaceHit(hit, latest.current));
    bump(caretKey(hit.nodeId, hit.itemId));
  }, [hits, hitIndex, replaceHit, commit, bump]);

  const replaceAll = useCallback(() => {
    if (!searchPattern(search)) return;
    let next = latest.current;
    // Von hinten ersetzen, damit vorherige Trefferpositionen gültig bleiben.
    for (const hit of [...collectHits(next, search)].reverse()) next = replaceHit(hit, next);
    commit(next);
    setSyncTokens(tokens => Object.fromEntries(Object.keys(tokens).map(key => [key, (tokens[key] || 0) + 1])));
  }, [search, replaceHit, commit]);

  // ---------- Tastenkürzel ----------

  const handleShortcut = useCallback((event: React.KeyboardEvent) => {
    const control = event.ctrlKey || event.metaKey;
    if (!control) {
      if (event.key === 'Escape') { (document.activeElement as HTMLElement | null)?.blur(); }
      if (event.altKey && event.shiftKey && (event.key === 'ArrowUp' || event.key === 'ArrowDown') && selected) {
        event.preventDefault();
        move(selected, event.key === 'ArrowUp' ? -1 : 1);
      }
      return;
    }
    const key = event.key.toLowerCase();
    const run = (action: () => void) => { event.preventDefault(); action(); };
    if (key === 'z' && !event.shiftKey) return run(undo);
    if (key === 'y' || (key === 'z' && event.shiftKey)) return run(redo);
    // Über event.code, damit die Kürzel auf allen Tastaturbelegungen greifen.
    const digit = /^Digit([0-9])$/.exec((event as any).code || '')?.[1];
    if (event.altKey && digit && Number(digit) <= 6) return run(() => (digit === '0' ? convert('paragraph') : convert('heading', Number(digit) as any)));
    if (key === 'b') return run(() => doToggleMark('bold'));
    if (key === 'i') return run(() => doToggleMark('italic'));
    if (key === 'u') return run(() => doToggleMark('underline'));
    if (key === 's' && event.shiftKey) return run(() => doToggleMark('strike'));
    if (key === 'k') return run(() => insertLink());
    if (key === 'f') return run(() => setShowFind(true));
    // Ausrichtung mit Umschalt, damit Browserkürzel wie Strg+R erhalten bleiben.
    if (event.shiftKey && key === 'l') return run(() => setStyle({ align: 'left' }));
    if (event.shiftKey && key === 'e') return run(() => setStyle({ align: 'center' }));
    if (event.shiftKey && key === 'r') return run(() => setStyle({ align: 'right' }));
    if (event.shiftKey && key === 'j') return run(() => setStyle({ align: 'justify' }));
    if (key === 'd' && selected) return run(() => duplicate(selected));
    if (key === ' ') return run(clearFormatting);
    if (event.shiftKey && key === 'c') return run(copyFormat);
    if (event.shiftKey && key === 'v' && painter) return run(pasteFormat);
    if (event.shiftKey && digit === '7') return run(() => toggleList(true));
    if (event.shiftKey && digit === '8') return run(() => toggleList(false));
    if (event.key === 'Enter') return run(() => insert({ id: uid(), type: 'pageBreak' }));
    return undefined;
  }, [selected, undo, redo, convert, doToggleMark, insertLink, setStyle, duplicate, clearFormatting, copyFormat, pasteFormat, painter, toggleList, insert, move]);

  // ---------- Seitenschätzung ----------

  useEffect(() => {
    const element = flow.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const contentHeightPx = geometry.contentHeightMm * PX_PER_MM;
    const measure = () => {
      const explicit = value.nodes.filter(entry => entry.type === 'pageBreak' || entry.style?.pageBreakBefore || entry.style?.pageBreakAfter).length;
      setPageCount(Math.max(1, Math.ceil(element.scrollHeight / zoom / contentHeightPx) + explicit));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [value, zoom, geometry.contentHeightMm]);

  // Passt den Zoom automatisch an die verfügbare Breite an, damit die Seite nicht
  // hinter der Seitenleiste verschwindet – bis der Nutzer den Zoom manuell ändert.
  useEffect(() => {
    const element = canvas.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const pageWidth = geometry.widthMm * PX_PER_MM;
    const fit = () => {
      if (!autoZoom.current) return;
      const available = element.clientWidth - 32; // abzüglich der Innenabstände (p-4)
      if (available <= 0) return;
      const next = Math.max(0.5, Math.min(1, Math.floor((available / pageWidth) * 100) / 100));
      setZoom(current => (Math.abs(current - next) < 0.01 ? current : next));
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(element);
    return () => observer.disconnect();
  }, [geometry.widthMm]);

  const changeZoom = useCallback((next: number) => {
    autoZoom.current = false;
    setZoom(next);
  }, []);

  const api: EditorApi = {
    document: value, node, flags, style: resolvedStyle, hasSelection, canUndo: history.current.length > 0, canRedo: future.current.length > 0,
    zoom, painter: !!painter, variables,
    undo, redo, toggleMark: doToggleMark, setMark, clearFormatting, setStyle, setDocumentNode, setDocument, insert, convert, toggleList,
    indent, applyStyleRef, createStyleFromNode, updateStyleFromNode, copyFormat, pasteFormat, insertVariable, insertLink,
    toggleFind: () => setShowFind(open => !open), setZoom: changeZoom,
  };

  const bridge = {
    document: value, selectedId: selected, activeItemId, readOnly, headingNumbers: numbers, syncTokens, caret, autoFocusKey, dragged,
    select: (nodeId: string, itemId?: string) => { setSelected(nodeId); setActiveItemId(itemId); },
    update, remove, duplicate, move, setDragged, reorder, moveInto,
    onSelection: (nodeId: string, itemId: string | undefined, next: TextRange | null) => setTextSelection(next ? { nodeId, itemId, ...next } : null),
    onSplit, onMergeBackward, onMergeForward, onIndent,
  };

  const pageWidthPx = geometry.widthMm * PX_PER_MM;
  const contentHeightPx = geometry.contentHeightMm * PX_PER_MM;

  return (
    <div className="space-y-3" onKeyDown={handleShortcut}>
      <Toolbar api={api} />
      {showFind && (
        <FindReplace
          options={search}
          setOptions={next => { setSearch(next); setHitIndex(0); }}
          hits={hits}
          index={hitIndex}
          onNavigate={navigateHit}
          onReplace={replaceCurrent}
          onReplaceAll={replaceAll}
          onClose={() => setShowFind(false)}
        />
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[210px_1fr_260px] gap-4 items-start">
        <ElementPalette api={api}>
          {blocks.length > 0 && (
            <>
              <h3 className="font-semibold text-sm mt-5 mb-2">Bauteile</h3>
              <div className="space-y-1.5 max-h-64 overflow-auto">
                {blocks.map(block => (
                  <button
                    key={block.id}
                    type="button"
                    draggable
                    onDragStart={event => { event.dataTransfer.setData('application/x-contwave-block', block.id); event.dataTransfer.effectAllowed = 'copy'; }}
                    onClick={() => addBlock(block)}
                    className="w-full text-left border border-gray-200 rounded p-2 text-xs hover:bg-primary-50 cursor-grab"
                    title="In das Dokument ziehen oder anklicken"
                  >
                    {block.name}
                  </button>
                ))}
              </div>
            </>
          )}
          <Outline document={value} numbers={numbers} onSelect={setSelected} />
          <p className="mt-4 text-[11px] text-gray-500 leading-relaxed">
            Strg+B/I/U formatiert, Strg+Alt+1…6 setzt Überschriften, Tab ändert die Listenebene,
            Alt+Umschalt+↑/↓ verschiebt Elemente, Strg+F sucht, Strg+Enter fügt einen Seitenumbruch ein.
          </p>
        </ElementPalette>

        <section
          ref={canvas}
          className="bg-gray-100 rounded-lg p-4 overflow-auto min-w-0"
          aria-label="Dokument"
          onDragOver={event => { if (event.dataTransfer.types.includes('application/x-contwave-block')) event.preventDefault(); }}
          onDrop={event => {
            const blockId = event.dataTransfer.getData('application/x-contwave-block');
            if (!blockId) return;
            event.preventDefault();
            const block = blocks.find(item => item.id === blockId);
            if (block) addBlock(block);
          }}
        >
          <Ruler page={page} zoom={zoom} onChange={next => setDocument({ page: { ...page, ...next } })} />
          <div className="mx-auto" style={{ width: pageWidthPx * zoom }}>
            <div
              className="document-page bg-white shadow-lg mx-auto relative"
              style={{
                ...styleToCss(value, value.defaults),
                width: pageWidthPx,
                minHeight: geometry.heightMm * PX_PER_MM,
                marginTop: 0,
                marginBottom: 0,
                paddingTop: page.marginTop * PX_PER_MM,
                paddingRight: page.marginRight * PX_PER_MM,
                paddingBottom: page.marginBottom * PX_PER_MM,
                paddingLeft: page.marginLeft * PX_PER_MM,
                transform: zoom === 1 ? undefined : `scale(${zoom})`,
                transformOrigin: 'top left',
              }}
            >
              <div
                ref={flow}
                className="relative space-y-1"
                style={{ columnCount: (page.columns || 1) > 1 ? page.columns : undefined, columnGap: (page.columnGap || 8) * PX_PER_MM }}
              >
                {!value.nodes.length && (
                  <button
                    type="button"
                    className="border-2 border-dashed border-gray-300 rounded-lg w-full p-12 text-gray-500 hover:border-primary-400"
                    onClick={() => insert({ id: uid(), type: 'paragraph', text: '' })}
                  >
                    Erstes Element einfügen
                  </button>
                )}
                <DesignerContext.Provider value={bridge}>
                  {value.nodes.map(entry => (
                    <NodeFrame key={entry.id} node={entry}>
                      <NodeView node={entry} />
                    </NodeFrame>
                  ))}
                </DesignerContext.Provider>
              </div>
              {Array.from({ length: Math.max(0, pageCount - 1) }, (_unused, index) => (
                <div
                  key={index}
                  aria-hidden="true"
                  className="pointer-events-none absolute left-0 right-0 border-t border-dashed border-primary-200"
                  style={{ top: page.marginTop * PX_PER_MM + (index + 1) * contentHeightPx }}
                />
              ))}
            </div>
          </div>
        </section>

        <Inspector api={api} />
      </div>

      <div className="flex flex-wrap gap-4 items-center text-xs text-gray-600 bg-white border border-gray-200 rounded-lg px-3 py-2" role="status" aria-label="Dokumentstatistik">
        <span>{statistics.words} Wörter</span>
        <span>{statistics.characters} Zeichen</span>
        <span>{statistics.paragraphs} Absätze</span>
        <span>{statistics.headings} Überschriften</span>
        <span>{statistics.tables} Tabellen</span>
        <span>{statistics.signatures} Signaturfelder</span>
        <span>ca. {pageCount} {pageCount === 1 ? 'Seite' : 'Seiten'}</span>
        <span className="ml-auto">{historyTick > 0 ? `${history.current.length} Schritte rückgängig möglich` : 'Keine Änderungen'}</span>
      </div>
    </div>
  );
}

/** Navigierbare Gliederung der Überschriften. */
function Outline({ document: value, numbers, onSelect }: { document: DesignerDocument; numbers: Map<string, string>; onSelect: (id: string) => void }) {
  const headings = value.nodes.filter(node => node.type === 'heading');
  if (!headings.length) return null;
  return (
    <>
      <h3 className="font-semibold text-sm mt-5 mb-2">Gliederung</h3>
      <div className="space-y-0.5 max-h-52 overflow-auto">
        {headings.map(heading => (
          <button
            key={heading.id}
            type="button"
            onClick={() => { onSelect(heading.id); document.querySelector(`[data-node-id="${heading.id}"]`)?.scrollIntoView({ block: 'center', behavior: 'smooth' }); }}
            className="w-full text-left text-xs truncate hover:text-primary-600"
            style={{ paddingLeft: ((heading.level || 1) - 1) * 10 }}
          >
            {numbers.get(heading.id) ? `${numbers.get(heading.id)} ` : ''}{heading.text || 'Überschrift'}
          </button>
        ))}
      </div>
    </>
  );
}

/** Lineal mit ziehbaren Seitenrändern. */
function Ruler({ page, zoom, onChange }: { page: NonNullable<DesignerDocument['page']>; zoom: number; onChange: (patch: Partial<NonNullable<DesignerDocument['page']>>) => void }) {
  const geometry = pageGeometry(page);
  const widthPx = geometry.widthMm * PX_PER_MM * zoom;
  const drag = (side: 'marginLeft' | 'marginRight') => (event: React.PointerEvent<HTMLButtonElement>) => {
    event.preventDefault();
    const startX = event.clientX;
    const startValue = page[side];
    const scale = PX_PER_MM * zoom;
    let emitted = startValue;
    const onMove = (moveEvent: PointerEvent) => {
      const deltaMm = (moveEvent.clientX - startX) / scale * (side === 'marginRight' ? -1 : 1);
      const next = Math.round(Math.min(100, Math.max(0, startValue + deltaMm)));
      if (next === emitted) return;
      emitted = next;
      onChange({ [side]: next });
    };
    const onUp = () => { window.removeEventListener('pointermove', onMove); window.removeEventListener('pointerup', onUp); };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };
  const ticks = Math.floor(geometry.widthMm / 10);
  return (
    <div className="mx-auto mb-2 relative h-6 select-none" style={{ width: widthPx }} aria-label="Lineal">
      <div className="absolute inset-0 bg-white border border-gray-200 rounded-sm overflow-hidden">
        <div className="absolute top-0 bottom-0 bg-gray-100" style={{ left: 0, width: page.marginLeft * PX_PER_MM * zoom }} />
        <div className="absolute top-0 bottom-0 bg-gray-100" style={{ right: 0, width: page.marginRight * PX_PER_MM * zoom }} />
        {Array.from({ length: ticks + 1 }, (_unused, index) => (
          <span key={index} className="absolute top-0 text-[8px] text-gray-400 border-l border-gray-300 pl-0.5 h-full" style={{ left: index * 10 * PX_PER_MM * zoom }}>
            {index % 2 === 0 ? index : ''}
          </span>
        ))}
      </div>
      <button
        type="button"
        aria-label="Linken Seitenrand ziehen"
        onPointerDown={drag('marginLeft')}
        className="absolute -bottom-1 h-3 w-3 -ml-1.5 bg-primary-500 rounded-sm cursor-ew-resize"
        style={{ left: page.marginLeft * PX_PER_MM * zoom }}
      />
      <button
        type="button"
        aria-label="Rechten Seitenrand ziehen"
        onPointerDown={drag('marginRight')}
        className="absolute -bottom-1 h-3 w-3 -mr-1.5 bg-primary-500 rounded-sm cursor-ew-resize"
        style={{ right: page.marginRight * PX_PER_MM * zoom }}
      />
    </div>
  );
}
